/**
 * Legacy Workspace Converter
 *
 * Converts workspace data from WebCharts Antigo (legacy format) to Odin Sight format
 *
 * This module provides utilities to:
 * 1. Parse legacy workspace format from LegacyStorage API or file
 * 2. Convert to Odin Sight workspace structure
 * 3. Map legacy indicators to current indicator registry
 * 4. Convert legacy drawings to current format
 */

/**
 * Convert legacy workspace to Odin Sight workspace format
 * @param {Object} legacyData - Legacy workspace data from WebCharts Antigo
 * @returns {Object} - Odin Sight workspace format
 */
export const convertLegacyWorkspace = (legacyData) => {
  console.log("[LegacyConverter] Starting conversion...");
  console.log(
    "[LegacyConverter] Legacy data structure:",
    Object.keys(legacyData),
  );

  // TODO: Implement actual conversion logic based on legacy format
  // For now, we create a basic structure and log the data for analysis

  try {
    // Detect legacy format type
    const formatType = detectLegacyFormat(legacyData);
    console.log("[LegacyConverter] Detected format type:", formatType);

    // Convert based on detected format
    let workspace;
    switch (formatType) {
      case "MULTI_LAYOUT":
        workspace = convertMultiLayoutWorkspace(legacyData);
        break;
      case "SINGLE_CHART":
        workspace = convertSingleChartWorkspace(legacyData);
        break;
      case "LEGACY_STORAGE":
        workspace = convertLegacyStorageWorkspace(legacyData);
        break;
      case "GOLDENLAYOUT":
        workspace = convertGoldenLayoutWorkspace(legacyData);
        break;
      default:
        workspace = createEmptyWorkspace();
        console.warn(
          "[LegacyConverter] Unknown format, creating empty workspace",
        );
    }

    console.log("[LegacyConverter] Conversion complete");
    return workspace;
  } catch (error) {
    console.error("[LegacyConverter] Conversion failed:", error);
    return createEmptyWorkspace();
  }
};

/**
 * Detect the type of legacy format
 * @param {Object} legacyData - Legacy workspace data
 * @returns {string} - Format type identifier
 */
const detectLegacyFormat = (legacyData) => {
  // Check for common legacy format patterns
  if (legacyData.layouts && Array.isArray(legacyData.layouts)) {
    return "MULTI_LAYOUT";
  }

  if (legacyData.symbol && legacyData.periodicity) {
    return "SINGLE_CHART";
  }

  // LegacyStorage API format (from MongoDB) - testdata.json format
  if (
    legacyData.chartid !== undefined &&
    legacyData.content &&
    legacyData.kind
  ) {
    return "LEGACY_STORAGE";
  }

  // GoldenLayout format (parsed content from LEGACY_STORAGE)
  if (
    legacyData.settings &&
    legacyData.content &&
    Array.isArray(legacyData.content)
  ) {
    return "GOLDENLAYOUT";
  }

  // Check for ChartIQ format
  if (legacyData.layout || legacyData.drawings || legacyData.studies) {
    return "CHARTIQ_FORMAT";
  }

  return "UNKNOWN";
};

/**
 * Convert multi-layout workspace format
 * @param {Object} legacyData - Legacy data with multiple layouts
 * @returns {Object} - Odin Sight workspace
 */
const convertMultiLayoutWorkspace = (legacyData) => {
  console.log("[LegacyConverter] Converting multi-layout workspace...");

  const charts = legacyData.layouts.map((layout, index) => ({
    id: `chart-${Date.now()}-${index}`,
    symbol: extractSymbol(layout),
    periodicity: convertPeriodicity(layout.periodicity),
    chartType: convertChartType(layout.chartType),
    indicators: convertIndicators(layout.indicators || []),
    drawings: convertDrawings(layout.drawings || []),
    yAxisWidth: 60,
  }));

  return {
    charts,
    selectedTab: 0,
    chartsPanelWidth: 250,
  };
};

/**
 * Convert single chart workspace format
 * @param {Object} legacyData - Legacy data with single chart
 * @returns {Object} - Odin Sight workspace
 */
const convertSingleChartWorkspace = (legacyData) => {
  console.log("[LegacyConverter] Converting single chart workspace...");

  const chart = {
    id: `chart-${Date.now()}`,
    symbol: extractSymbol(legacyData),
    periodicity: convertPeriodicity(legacyData.periodicity),
    chartType: convertChartType(legacyData.chartType),
    indicators: convertIndicators(legacyData.indicators || []),
    drawings: convertDrawings(legacyData.drawings || []),
    yAxisWidth: 60,
  };

  return {
    charts: [chart],
    selectedTab: 0,
    chartsPanelWidth: 250,
  };
};

/**
 * Convert legacy storage API format (content field is JSON string)
 * @param {Object} legacyData - Data from LegacyStorage API (testdata.json format)
 * @returns {Object} - Odin Sight workspace
 */
const convertLegacyStorageWorkspace = (legacyData) => {
  console.log("[LegacyConverter] Converting legacy storage format...");
  console.log("[LegacyConverter] Legacy metadata:", {
    name: legacyData.name,
    chartid: legacyData.chartid,
    userid: legacyData.userid,
    timestamp: legacyData.timestamp,
  });

  try {
    // Parse content field if it's a string
    const content =
      typeof legacyData.content === "string"
        ? JSON.parse(legacyData.content)
        : legacyData.content;

    // Convert the GoldenLayout content
    const workspace = convertLegacyWorkspace(content);

    // Enhance workspace with original metadata
    if (workspace && workspace.metadata) {
      workspace.metadata.originalName = legacyData.name;
      workspace.metadata.originalChartId = legacyData.chartid;
      workspace.metadata.originalTimestamp = legacyData.timestamp;
      workspace.metadata.originalUserId = legacyData.userid;
      workspace.metadata.originalClientId = legacyData.clientid;
    }

    return workspace;
  } catch (error) {
    console.error(
      "[LegacyConverter] Failed to parse legacy storage content:",
      error,
    );
    return createEmptyWorkspace();
  }
};

/**
 * Convert GoldenLayout workspace format (from parsed content field)
 * @param {Object} goldenLayoutData - Parsed GoldenLayout structure
 * @returns {Object} - Odin Sight workspace
 */
const convertGoldenLayoutWorkspace = (goldenLayoutData) => {
  console.log("[LegacyConverter] Converting GoldenLayout workspace...");

  try {
    // Extract chart components from GoldenLayout structure
    const chartComponents = extractChartComponents(goldenLayoutData);

    if (!chartComponents || chartComponents.length === 0) {
      console.warn(
        "[LegacyConverter] No chart components found in GoldenLayout",
      );
      return createEmptyWorkspace();
    }

    console.log(
      `[LegacyConverter] Found ${chartComponents.length} chart components`,
    );

    // Convert each chart component to Odin format
    const charts = chartComponents.map((component, index) => {
      return convertChartComponent(component, index);
    });

    // Get selected tab from GoldenLayout
    const selectedTab = extractSelectedTab(goldenLayoutData);

    // Create Odin workspace structure
    return createBasicWorkspace(charts, selectedTab);
  } catch (error) {
    console.error("[LegacyConverter] Failed to convert GoldenLayout:", error);
    return createEmptyWorkspace();
  }
};

/**
 * Create empty workspace
 * @returns {Object} - Empty Odin Sight workspace
 */
const createEmptyWorkspace = () => {
  console.log("[LegacyConverter] Creating empty workspace...");

  return {
    version: "1.1",
    timestamp: new Date().toISOString(),
    charts: [
      {
        id: `chart-${Date.now()}`,
        symbol: "PETR4",
        periodicity: "1D",
        chartType: "candle",
        indicators: [],
        interactiveObjects: {},
        showGrid: true,
        sectionHeights: { 0: 1 }, // Phase 1: Single section (no indicators yet)
        sectionOrder: [0],
      },
    ],
    selectedTab: 0,
    activeInteractiveTools: {},
    // Don't include workspaceLayout - preserve user's current panel states
    metadata: {
      app: "Odin Sight",
      description: "Empty workspace",
      conversionDate: new Date().toISOString(),
    },
  };
};

// ============================================================================
// GOLDENLAYOUT PARSING HELPERS
// ============================================================================

/**
 * Extract chart components from GoldenLayout structure
 * @param {Object} goldenLayoutData - GoldenLayout configuration
 * @returns {Array} - Array of chart components
 */
const extractChartComponents = (goldenLayoutData) => {
  console.log(
    "[LegacyConverter] Extracting chart components from GoldenLayout...",
  );

  try {
    // Navigate GoldenLayout structure: content[0] (row) → content[1+] (stacks) → content[] (components)
    if (!goldenLayoutData.content || !Array.isArray(goldenLayoutData.content)) {
      console.warn("[LegacyConverter] No content array in GoldenLayout");
      return [];
    }

    const row = goldenLayoutData.content[0];
    if (!row || !row.content) {
      console.warn("[LegacyConverter] No row content in GoldenLayout");
      return [];
    }

    // Find stacks (skip orders panel which is usually first)
    const stacks = row.content.filter((item) => item.type === "stack");

    // Find the chart stack (contains chartComponent)
    const chartStack = stacks.find(
      (stack) =>
        stack.content &&
        stack.content.some((c) => c.componentName === "chartComponent"),
    );

    if (!chartStack) {
      console.warn("[LegacyConverter] No chart stack found in GoldenLayout");
      return [];
    }

    // Extract all chart components
    const chartComponents = chartStack.content.filter(
      (c) => c.componentName === "chartComponent",
    );

    // Store activeItemIndex for later
    chartComponents.activeItemIndex = chartStack.activeItemIndex || 0;

    console.log(
      `[LegacyConverter] Found ${chartComponents.length} chart components`,
    );
    return chartComponents;
  } catch (error) {
    console.error(
      "[LegacyConverter] Error extracting chart components:",
      error,
    );
    return [];
  }
};

/**
 * Extract selected tab index from GoldenLayout
 * @param {Object} goldenLayoutData - GoldenLayout configuration
 * @returns {number} - Selected tab index
 */
const extractSelectedTab = (goldenLayoutData) => {
  try {
    const row = goldenLayoutData.content[0];
    const stacks = row.content.filter((item) => item.type === "stack");
    const chartStack = stacks.find(
      (stack) =>
        stack.content &&
        stack.content.some((c) => c.componentName === "chartComponent"),
    );

    return chartStack?.activeItemIndex || 0;
  } catch (error) {
    console.warn("[LegacyConverter] Error extracting selected tab:", error);
    return 0;
  }
};

/**
 * Convert legacy panel layout to Odin section heights
 * @param {Object} panels - Legacy panels object from chartState
 * @param {Array} indicators - Converted indicators array
 * @returns {Object} - { sectionHeights, sectionOrder }
 */
const convertPanelLayout = (panels, indicators, convertedPanels) => {
  console.log("\n📐 PANEL LAYOUT CONVERSION");
  console.log("   Legacy panels:", JSON.stringify(panels, null, 2));
  console.log("   Number of indicators:", indicators?.length || 0);
  console.log("   Converted panels:", Array.from(convertedPanels || []));

  // If no indicators, single section with 100% height
  if (!indicators || indicators.length === 0) {
    console.log("   ✅ No indicators → Single section (100% height)");
    return {
      sectionHeights: { 0: 1 },
      sectionOrder: [0],
    };
  }

  // If panels information exists, use percent values
  if (panels && typeof panels === "object") {
    const sectionHeights = {};
    const sectionOrder = [];
    let index = 0;

    // Main chart panel (always section 0)
    const chartPanel = panels.chart || panels[Object.keys(panels)[0]];
    if (chartPanel && chartPanel.percent !== undefined) {
      sectionHeights[0] = chartPanel.percent;
      sectionOrder.push(0);
      index = 1;
    } else {
      // Default: 75% for main chart if no panel info
      sectionHeights[0] = 0.75;
      sectionOrder.push(0);
      index = 1;
    }

    // Add only panels that have successfully converted indicators
    let totalIndicatorHeight = 0;
    const indicatorPanels = [];

    for (const [panelName, panelData] of Object.entries(panels)) {
      if (panelName !== "chart" && panelData.percent !== undefined) {
        // Only include this panel if its indicator was converted
        if (convertedPanels && convertedPanels.has(panelName)) {
          console.log(
            `   ✅ Panel "${panelName}": ${panelData.percent * 100}%`,
          );
          indicatorPanels.push({ name: panelName, percent: panelData.percent });
          totalIndicatorHeight += panelData.percent;
        } else {
          console.log(
            `   ⏭️  Panel "${panelName}" skipped (indicator not converted)`,
          );
        }
      }
    }

    // Redistribute heights if some panels were skipped
    if (indicatorPanels.length > 0) {
      // Adjust main chart height to absorb skipped panels
      const skippedHeight =
        Object.values(panels)
          .filter((p) => p.percent !== undefined)
          .reduce((sum, p) => sum + p.percent, 0) -
        sectionHeights[0] -
        totalIndicatorHeight;

      if (skippedHeight > 0) {
        sectionHeights[0] += skippedHeight;
        console.log(
          `   📊 Redistributed ${(skippedHeight * 100).toFixed(1)}% from skipped panels to main chart`,
        );
      }

      // Add converted indicator panels
      for (const panel of indicatorPanels) {
        sectionHeights[index] = panel.percent;
        sectionOrder.push(index);
        index++;
      }
    } else {
      // No indicator panels, main chart gets 100%
      sectionHeights[0] = 1;
    }

    console.log("   ✅ Result:", { sectionHeights, sectionOrder });
    return { sectionHeights, sectionOrder };
  }

  // Default: Main chart 75%, indicators panel 25%
  console.log("   ✅ Default layout (75% / 25%)");
  return {
    sectionHeights: { 0: 0.75, 1: 0.25 },
    sectionOrder: [0, 1],
  };
};

/**
 * Convert a single chart component from GoldenLayout to Odin format
 * @param {Object} component - GoldenLayout chart component
 * @param {number} index - Chart index
 * @returns {Object} - Odin chart object
 */
const convertChartComponent = (component, index) => {
  console.log(
    `[LegacyConverter] Converting chart component ${index}: ${component.title || "Untitled"}`,
  );

  try {
    const chartState = component.componentState?.chartState || {};

    const symbol = extractSymbol(chartState);
    const periodicity = convertPeriodicity(chartState);
    const chartType = convertChartType(chartState.chartType);

    // Phase 2: Convert indicators from studies
    const { indicators, convertedPanels } = convertIndicators(
      chartState.studies || {},
    );

    // Phase 2: Calculate section heights based on panels (only include converted panels)
    const { sectionHeights, sectionOrder } = convertPanelLayout(
      chartState.panels,
      indicators,
      convertedPanels,
    );

    console.log(
      `[LegacyConverter]   ✓ Symbol: ${symbol}, Type: ${chartType}, Period: ${periodicity}, Indicators: ${indicators.length}`,
    );

    return {
      id: `chart-${Date.now()}-${index}`,
      symbol: symbol,
      periodicity: periodicity,
      chartType: chartType,
      indicators: indicators,
      interactiveObjects: {},
      showGrid: true,
      sectionHeights: sectionHeights,
      sectionOrder: sectionOrder,
    };
  } catch (error) {
    console.error(`[LegacyConverter] Error converting chart ${index}:`, error);
    // Return a default chart on error
    return {
      id: `chart-${Date.now()}-${index}`,
      symbol: "PETR4",
      periodicity: "1D",
      chartType: "candle",
      indicators: [],
      interactiveObjects: {},
      showGrid: true,
      sectionHeights: { 0: 1 },
      sectionOrder: [0],
    };
  }
};

/**
 * Create basic Odin workspace from converted charts
 * @param {Array} charts - Array of converted charts
 * @param {number} selectedTab - Selected tab index
 * @returns {Object} - Complete Odin workspace
 */
const createBasicWorkspace = (charts, selectedTab = 0) => {
  console.log("[LegacyConverter] Creating basic workspace structure...");

  return {
    version: "1.1",
    timestamp: new Date().toISOString(),
    charts: charts,
    selectedTab: selectedTab,
    activeInteractiveTools: {},
    // Don't include workspaceLayout - preserve user's current panel states
    // Legacy WebCharts doesn't have side panels, so we shouldn't override them
    metadata: {
      app: "Odin Sight",
      description: "Converted from WebCharts Antigo",
      conversionDate: new Date().toISOString(),
      conversionVersion: "1.0",
    },
  };
};

// ============================================================================
// CONVERSION HELPERS
// ============================================================================

/**
 * Extract symbol from legacy chartState
 * @param {Object} chartState - Legacy chart state from GoldenLayout
 * @returns {string} - Symbol ticker
 */
const extractSymbol = (chartState) => {
  // Priority order for symbol detection

  // 1. From symbols array (most reliable in ChartIQ format)
  if (
    chartState.symbols &&
    Array.isArray(chartState.symbols) &&
    chartState.symbols.length > 0
  ) {
    const symbolObj = chartState.symbols[0];
    if (symbolObj.symbol) {
      return symbolObj.symbol;
    }
  }

  // 2. From display field
  if (chartState.display) {
    return chartState.display;
  }

  // 3. From panels.chart.display
  if (
    chartState.panels &&
    chartState.panels.chart &&
    chartState.panels.chart.display
  ) {
    return chartState.panels.chart.display;
  }

  // 4. Direct symbol property (fallback)
  if (chartState.symbol) {
    return chartState.symbol;
  }

  console.warn(
    "[LegacyConverter] Symbol not found in chartState, using default PETR4",
  );
  return "PETR4";
};

/**
 * Convert legacy periodicity to Odin Sight format
 * @param {Object} chartState - Legacy chart state containing periodicity info
 * @returns {string} - Odin Sight periodicity string (e.g., '1D', '5min', '60min')
 */
const convertPeriodicity = (chartState) => {
  // Extract periodicity from chartState or symbols[0]
  let interval, periodicity, timeUnit;

  // Try to get from symbols array first
  if (chartState.symbols && chartState.symbols.length > 0) {
    const symbolObj = chartState.symbols[0];
    interval = symbolObj.interval || chartState.interval;
    periodicity = symbolObj.periodicity || chartState.periodicity;
    timeUnit = symbolObj.timeUnit || chartState.timeUnit;
  } else {
    // Fallback to direct chartState properties
    interval = chartState.interval;
    periodicity = chartState.periodicity;
    timeUnit = chartState.timeUnit;
  }

  // Default values
  if (interval === undefined) interval = "day";
  if (periodicity === undefined) periodicity = 1;

  // Handle intraday (minute-based) periodicity
  // Legacy format: interval=5, periodicity=3, timeUnit='minute' → 15 minute chart (15min)
  if (timeUnit === "minute") {
    const totalMinutes = periodicity * interval;
    return `${totalMinutes}min`;
  }

  // Handle daily/weekly/monthly
  if (interval === "day") {
    return "1D";
  }
  if (interval === "week") {
    return "1W";
  }
  if (interval === "month") {
    return "1M";
  }

  // Default to daily if unclear
  console.warn(
    "[LegacyConverter] Unclear periodicity format, defaulting to 1D",
  );
  return "1D";
};

/**
 * Convert legacy chart type to Odin Sight format
 * @param {string} legacyChartType - Legacy chart type
 * @returns {string} - Odin Sight chart type
 */
const convertChartType = (legacyChartType) => {
  // TODO: Implement chart type mapping
  // Map legacy chart types to Odin Sight types

  const chartTypeMap = {
    candle: "candle",
    candlestick: "candle",
    bar: "bar",
    ohlc: "bar",
    line: "line",
    area: "area",
    heikinashi: "heikinashi",
    renko: "renko",
    kagi: "kagi",
    linebreak: "linebreak",
    pointfigure: "pointfigure",
  };

  const normalized = (legacyChartType || "candle").toLowerCase();
  return chartTypeMap[normalized] || "candle";
};

/**
 * Convert a single legacy indicator to Odin Sight format
 * @param {Object} study - Legacy study object
 * @param {number} timestamp - Base timestamp for ID generation
 * @param {number} index - Index for unique ID
 * @returns {Object|null} - Odin Sight indicator or null if conversion fails
 */
const convertSingleIndicator = (study, timestamp, index) => {
  if (!study || !study.type) {
    return null;
  }

  // Map legacy type to Odin key
  let key = mapLegacyIndicatorType(study.type);
  if (!key) {
    console.warn(`[LegacyConverter] Unknown indicator type: ${study.type}`);
    return null;
  }

  // Convert inputs to config
  const config = convertIndicatorInputs(study.type, study.inputs || {});

  // For "ma" type, determine specific type (sma/ema/wma) from inputs
  if (key === "ma" && study.inputs && study.inputs.Type) {
    const specificType = convertMAType(study.inputs.Type);
    key = specificType; // Change 'ma' to 'sma', 'ema', or 'wma'
  }

  // Convert outputs to appearanceConfig
  const appearanceConfig = convertIndicatorOutputs(
    study.type,
    study.outputs || {},
  );

  return {
    key,
    id: `indicator-${timestamp}-${index}`,
    config,
    appearanceConfig,
  };
};

/**
 * Convert legacy indicators to Odin Sight format
 * @param {Object} legacyStudies - Legacy studies object from chartState
 * @returns {Array} - Array of Odin Sight indicators
 */
// Module-level tracking for conversion issues
let conversionIssues = {
  skipped: [],
  failed: [],
};

export const convertIndicators = (legacyStudies) => {
  if (!legacyStudies || typeof legacyStudies !== "object") {
    return { indicators: [], convertedPanels: new Set() };
  }

  // Reset tracking for new conversion
  conversionIssues = {
    skipped: [],
    failed: [],
  };

  console.log("\n" + "=".repeat(80));
  console.log("📊 LEGACY INDICATORS - ORIGINAL FORMAT");
  console.log("=".repeat(80));
  console.log("Raw studies object:", JSON.stringify(legacyStudies, null, 2));
  console.log("=".repeat(80) + "\n");

  const indicators = [];
  const convertedPanels = new Set();
  const timestamp = Date.now();
  let index = 0;

  // Legacy format: studies is an object where each key is an indicator instance
  // Example: { "‌ma‌ (21,ma,0)": { type: "ma", inputs: {...}, outputs: {...} } }
  for (const [displayName, study] of Object.entries(legacyStudies)) {
    try {
      console.log(`\n📌 Converting indicator ${index + 1}: "${displayName}"`);
      console.log("   Legacy type:", study.type);
      console.log("   Legacy inputs:", JSON.stringify(study.inputs, null, 2));
      console.log("   Legacy outputs:", JSON.stringify(study.outputs, null, 2));
      console.log("   Legacy panel:", study.panel);

      const indicator = convertSingleIndicator(study, timestamp, index);

      if (indicator) {
        console.log("   ✅ Converted to:");
        console.log("      Odin key:", indicator.key);
        console.log(
          "      Odin config:",
          JSON.stringify(indicator.config, null, 2),
        );
        console.log(
          "      Odin appearanceConfig:",
          JSON.stringify(indicator.appearanceConfig, null, 2),
        );
        indicators.push(indicator);

        // Track panel for this successfully converted indicator
        if (study.panel && study.panel !== "chart") {
          convertedPanels.add(study.panel);
        }

        index++;
      } else {
        console.warn(`   ❌ Skipped (not supported or conversion failed)`);
        conversionIssues.skipped.push({
          name: displayName,
          type: study.type,
          reason: "Indicador não implementado no Odin Sight",
        });
      }
    } catch (error) {
      console.warn(
        `   ❌ Failed to convert indicator "${displayName}":`,
        error,
      );
      conversionIssues.failed.push({
        name: displayName,
        type: study.type,
        error: error.message || "Erro desconhecido",
      });
    }
  }

  console.log("\n" + "=".repeat(80));
  console.log(
    `✅ CONVERSION COMPLETE: ${indicators.length} indicators converted`,
  );
  if (conversionIssues.skipped.length > 0) {
    console.log(
      `⚠️  SKIPPED: ${conversionIssues.skipped.length} indicators not implemented`,
    );
  }
  if (conversionIssues.failed.length > 0) {
    console.log(
      `❌ FAILED: ${conversionIssues.failed.length} indicators failed to convert`,
    );
  }
  console.log("=".repeat(80) + "\n");

  return { indicators, convertedPanels };
};

/**
 * Convert legacy indicator inputs to Odin config
 * @param {string} type - Legacy indicator type
 * @param {Object} inputs - Legacy inputs object
 * @returns {Object} - Odin config object
 */
const convertIndicatorInputs = (type, inputs) => {
  const config = {};
  const normalizedType = type.toLowerCase().trim();

  // Moving Average
  if (normalizedType === "ma") {
    const period = parseInt(inputs.Period || inputs.period || 10);
    config.windowSize = period;
    config.sourcePath =
      inputs.Field === "field" ? "close" : inputs.Field || "close";
    // Deslocamento no tempo do estudo unificado Moving Average — vale para as 8
    // variantes. Sem isto o template importava a média no lugar errado, calado.
    config.offsetBars = convertOffsetBars(inputs.Offset);
    // Note: MA type is determined from inputs.Type in convertSingleIndicator
  }
  // RSI
  else if (normalizedType === "rsi") {
    config.windowSize = parseInt(inputs.Period || 14);
    config.sourcePath =
      inputs.Field === "field" ? "close" : inputs.Field || "close";
  }
  // MACD
  else if (normalizedType === "macd") {
    config.fast = parseInt(inputs["Fast MA Period"] || 12);
    config.slow = parseInt(inputs["Slow MA Period"] || 26);
    config.signal = parseInt(inputs["Signal Period"] || 9);
  }
  // Bollinger Bands
  else if (normalizedType === "bollinger bands") {
    config.windowSize = parseInt(inputs.Period || 20);
    config.sourcePath =
      inputs.Field === "field" ? "close" : inputs.Field || "close";
    config.multiplier = parseFloat(inputs["Standard Deviations"] || 2);
    config.movingAverageType = convertMAType(
      inputs["Moving Average Type"] || "sma",
    );
  }
  // ADX
  else if (normalizedType === "adx") {
    config.windowSize = parseInt(inputs.Period || 14);
  }
  // Stochastic
  else if (
    normalizedType === "stochastics" ||
    normalizedType === "stochastic"
  ) {
    config.windowSize = parseInt(inputs.Period || 14);
    config.kPeriod = parseInt(inputs.Period || 14);
    config.dPeriod = parseInt(inputs["%D Period"] || 3);
  }
  // Volume - no inputs needed
  else if (normalizedType === "volume") {
    // Volume typically has no config
  }
  // VWAP - no inputs needed
  else if (normalizedType === "vwap") {
    // VWAP typically has no config
  }
  // CCI
  else if (normalizedType === "cci") {
    config.windowSize = parseInt(inputs.Period || 20);
  }
  // On Balance Volume - no inputs needed
  else if (normalizedType === "on balance volume" || normalizedType === "obv") {
    // OBV typically has no config
  }
  // Money Flow Index
  else if (normalizedType === "money flow index" || normalizedType === "mfi") {
    config.windowSize = parseInt(inputs.Period || 14);
  }
  // ATR
  else if (normalizedType === "atr") {
    config.windowSize = parseInt(inputs.Period || 14);
  }
  // DIDI
  else if (normalizedType === "didi") {
    config.highPeriod = parseInt(inputs["High Period"] || inputs.High || 3);
    config.midPeriod = parseInt(
      inputs["Mid Period"] || inputs.Center || inputs.Mid || 8,
    );
    config.lowPeriod = parseInt(inputs["Low Period"] || inputs.Low || 20);
    config.sourcePath =
      inputs.Field === "field" ? "close" : inputs.Field || "close";
  }
  // Schaff Trend Cycle
  else if (
    normalizedType === "schaff" ||
    normalizedType === "schaff trend cycle"
  ) {
    config.period = parseInt(
      inputs.Period || inputs["Stochastic Period"] || 10,
    );
    config.shortCycle = parseInt(
      inputs["Short Cycle"] || inputs.ShortCycle || 23,
    );
    config.longCycle = parseInt(inputs["Long Cycle"] || inputs.LongCycle || 50);
    config.overBought = parseFloat(
      inputs["Over Bought"] || inputs.OverBought || 75,
    );
    config.overSold = parseFloat(inputs["Over Sold"] || inputs.OverSold || 25);
  }
  // Alligator
  else if (normalizedType === "alligator") {
    config.jawPeriod = parseInt(inputs["Jaw Period"] || 13);
    config.jawOffset = parseInt(inputs["Jaw Offset"] || 8);
    config.teethPeriod = parseInt(inputs["Teeth Period"] || 8);
    config.teethOffset = parseInt(inputs["Teeth Offset"] || 5);
    config.lipsPeriod = parseInt(inputs["Lips Period"] || 5);
    config.lipsOffset = parseInt(inputs["Lips Offset"] || 3);
  }
  // Awesome Oscillator
  else if (
    normalizedType === "awesome" ||
    normalizedType === "awesome oscillator"
  ) {
    // Awesome Oscillator has no config parameters
  }
  // Pivot Points
  else if (normalizedType === "pivot points") {
    config.type = inputs.Type || "fibonacci";
  }
  // Supertrend
  else if (normalizedType === "supertrend") {
    config.period = parseInt(inputs.Period || 7);
    config.multiplier = parseFloat(inputs.Multiplier || 3);
  }
  // ZigZag
  else if (normalizedType === "zigzag" || normalizedType === "zig zag") {
    config.distance = parseFloat(
      inputs["Distance(%)"] || inputs.Distance || 10,
    );
  }
  // ATR Trailing Stop
  else if (
    normalizedType === "atr trailing stop" ||
    normalizedType === "atr stop"
  ) {
    config.period = parseInt(inputs.Period || 14);
    config.multiplier = parseFloat(inputs.Multiplier || 3);
  }
  // Williams A/D
  else if (
    normalizedType === "w acc dist" ||
    normalizedType === "williams acc dist"
  ) {
    config.useVolume = inputs["Use Volume"] !== false;
  }
  // High Low
  else if (normalizedType === "high low") {
    config.highPeriod = parseInt(inputs.Period || 10);
    config.lowPeriod = parseInt(inputs.Period || 10);
    config.highShift = parseFloat(inputs["Shift Percentage"] || 5);
    config.lowShift = parseFloat(inputs["Shift Percentage"] || 5);
  }
  // Median Price
  else if (
    normalizedType === "med price" ||
    normalizedType === "median price"
  ) {
    config.windowSize = parseInt(inputs.Period || 30);
  }
  // Keltner
  else if (normalizedType === "keltner") {
    config.period = parseInt(inputs.Period || 20);
    config.shift = parseFloat(inputs.Shift || inputs.Multiplier || 2);
    config.maType = convertMAType(inputs["Moving Average Type"] || "ema");
  }
  // Bollinger Bandwidth
  else if (
    normalizedType === "boll bw" ||
    normalizedType === "bollinger bandwidth"
  ) {
    config.period = parseInt(inputs.Period || 20);
    config.multiplier = parseFloat(inputs["Standard Deviations"] || 2);
    config.maType = convertMAType(inputs["Moving Average Type"] || "sma");
  }
  // Klinger
  else if (normalizedType === "klinger") {
    config.signalPeriod = parseInt(inputs["Signal Periods"] || 13);
    config.shortCycle = parseInt(inputs["Short Cycle"] || 34);
    config.longCycle = parseInt(inputs["Long Cycle"] || 55);
  }
  // Volume Profile
  else if (
    normalizedType === "vol profile" ||
    normalizedType === "volume profile"
  ) {
    // Volume Profile has no config parameters in basic form
  }
  // Trend Intensity (193 instances - high priority)
  else if (
    normalizedType === "trend int" ||
    normalizedType === "trend intensity"
  ) {
    config.period = parseInt(inputs.Period || 14);
  }
  // Volume Rate of Change (144 instances - high priority)
  else if (normalizedType === "vol roc" || normalizedType === "volume roc") {
    config.period = parseInt(inputs.Period || 14);
  }
  // Stochastic Momentum (138 instances - high priority)
  else if (
    normalizedType === "stch mtm" ||
    normalizedType === "stochastic momentum"
  ) {
    config.kPeriod = parseInt(inputs["%K Period"] || inputs.Period || 14);
    config.kSmooth = parseInt(inputs["%K Smoothing"] || 3);
    config.dPeriod = parseInt(inputs["%D Period"] || 3);
  }
  // Valuation Lines (129 instances - high priority)
  else if (
    normalizedType === "val lines" ||
    normalizedType === "valuation lines"
  ) {
    config.period = parseInt(inputs.Period || 20);
  }
  // Trade Volume Index (90 instances - high priority)
  else if (
    normalizedType === "trade vol" ||
    normalizedType === "trade volume"
  ) {
    config.minTickValue = parseFloat(inputs["Minimum Tick Value"] || 0.5);
  }
  // PMO - Price Momentum Oscillator (75 instances - high priority)
  else if (normalizedType === "pmo") {
    config.shortPeriod = parseInt(inputs["Short Period"] || 35);
    config.longPeriod = parseInt(inputs["Long Period"] || 20);
    config.signalPeriod = parseInt(inputs["Signal Period"] || 10);
  }
  // Ultimate Oscillator (63 instances - high priority)
  else if (
    normalizedType === "ultimate" ||
    normalizedType === "ultimate oscillator"
  ) {
    config.period1 = parseInt(inputs.Period1 || inputs["Cycle 1"] || 7);
    config.period2 = parseInt(inputs.Period2 || inputs["Cycle 2"] || 14);
    config.period3 = parseInt(inputs.Period3 || inputs["Cycle 3"] || 28);
  }
  // Negative Volume Index (61 instances - high priority)
  else if (
    normalizedType === "neg vol" ||
    normalizedType === "negative volume"
  ) {
    // NVI typically has no config
  }
  // Bollinger %b (50 instances - high priority)
  else if (normalizedType === "boll %b" || normalizedType === "bollinger %b") {
    config.period = parseInt(inputs.Period || 20);
    config.multiplier = parseFloat(inputs["Standard Deviations"] || 2);
    config.maType = convertMAType(inputs["Moving Average Type"] || "sma");
  }
  // Disparity (50 instances - high priority)
  else if (normalizedType === "disparity") {
    config.period = parseInt(inputs.Period || 14);
    config.maType = convertMAType(inputs["Moving Average Type"] || "sma");
  }
  // Additional common indicators
  // Typical Price
  else if (normalizedType === "typical price") {
    config.period = parseInt(inputs.Period || 1);
  }
  // Positive Volume Index
  else if (
    normalizedType === "pos vol" ||
    normalizedType === "positive volume"
  ) {
    // PVI typically has no config
  }
  // Mass Index
  else if (normalizedType === "mass idx" || normalizedType === "mass index") {
    config.period = parseInt(inputs.Period || 25);
    config.emaPeriod = parseInt(inputs["EMA Period"] || 9);
  }
  // Coppock Curve
  else if (normalizedType === "coppock") {
    config.shortROC = parseInt(inputs["Short ROC"] || 11);
    config.longROC = parseInt(inputs["Long ROC"] || 14);
    config.wmaPeriod = parseInt(inputs["WMA Period"] || 10);
  }
  // Price ROC
  else if (normalizedType === "price roc") {
    config.period = parseInt(inputs.Period || 12);
  }
  // Darvas Box
  else if (normalizedType === "darvas" || normalizedType === "darvas box") {
    config.boxPeriod = parseInt(inputs["Box Period"] || 5);
    config.ATRPeriod = parseInt(inputs["ATR Period"] || 5);
  }
  // Historical Volatility
  else if (
    normalizedType === "hist vol" ||
    normalizedType === "historical volatility"
  ) {
    config.period = parseInt(inputs.Period || 10);
    config.annualizationFactor = parseFloat(
      inputs["Annualization Factor"] || 252,
    );
  }
  // Standard Deviation
  else if (
    normalizedType === "std dev" ||
    normalizedType === "standard deviation"
  ) {
    config.period = parseInt(inputs.Period || 14);
    config.maType = convertMAType(inputs["Moving Average Type"] || "sma");
  }

  return config;
};

/**
 * Convert legacy indicator outputs to Odin appearanceConfig
 * @param {string} type - Legacy indicator type
 * @param {Object} outputs - Legacy outputs object (colors)
 * @returns {Object} - Odin appearanceConfig object
 */
const convertIndicatorOutputs = (type, outputs) => {
  const appearanceConfig = {};
  const normalizedType = type.toLowerCase().trim();

  // Helper to convert color (handles "auto" → default color)
  const convertColor = (color, defaultColor = "#2196F3") => {
    if (color === "auto" || !color) return defaultColor;
    return color;
  };

  // Moving Average (single line)
  if (normalizedType === "ma") {
    const color = convertColor(outputs.MA || outputs.Result, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // RSI (single line)
  else if (normalizedType === "rsi") {
    const color = convertColor(outputs.RSI || outputs.Result, "#9C27B0");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // MACD (multiple lines and histogram)
  else if (normalizedType === "macd") {
    appearanceConfig.macd = {
      color: convertColor(outputs.MACD, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.signal = {
      color: convertColor(outputs.Signal, "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.histogram = {
      upColor: convertColor(outputs["Increasing Bar"], "#00DD00"),
      downColor: convertColor(outputs["Decreasing Bar"], "#FF0000"),
      opacity: 1,
    };
  }
  // Volume (up/down bars)
  else if (normalizedType === "volume") {
    appearanceConfig.upColor = convertColor(outputs["Up Volume"], "#8cc176");
    appearanceConfig.downColor = convertColor(
      outputs["Down Volume"],
      "#b82c0c",
    );
  }
  // ATR (single line)
  else if (normalizedType === "atr") {
    const color = convertColor(outputs.ATR || outputs.Result, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // DIDI (three lines)
  else if (normalizedType === "didi") {
    appearanceConfig.longStroke = {
      color: convertColor(outputs.Long || outputs["Long Line"], "#ff0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.shortStroke = {
      color: convertColor(outputs.Short || outputs["Short Line"], "#00ff00"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.centerStroke = {
      color: convertColor(outputs.Center || outputs["Center Line"], "#0000ff"),
      dash: "Dot",
      width: 1,
      opacity: 1,
    };
  }
  // Schaff Trend Cycle (single line + thresholds)
  else if (
    normalizedType === "schaff" ||
    normalizedType === "schaff trend cycle"
  ) {
    const color = convertColor(
      outputs.STC || outputs.Schaff || outputs.Result,
      "#2196F3",
    );
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    // Thresholds are part of config, not appearance in Odin
  }
  // Alligator (3 lines)
  else if (normalizedType === "alligator") {
    appearanceConfig.jawStroke = {
      color: convertColor(outputs.Jaw, "#0000FF"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.teethStroke = {
      color: convertColor(outputs.Teeth, "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.lipsStroke = {
      color: convertColor(outputs.Lips, "#00DD00"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Awesome Oscillator (histogram)
  else if (
    normalizedType === "awesome" ||
    normalizedType === "awesome oscillator"
  ) {
    appearanceConfig.upColor = convertColor(
      outputs["Increasing Bar"],
      "#00DD00",
    );
    appearanceConfig.downColor = convertColor(
      outputs["Decreasing Bar"],
      "#FF0000",
    );
  }
  // Pivot Points (multiple lines)
  else if (normalizedType === "pivot points") {
    appearanceConfig.pivotStroke = {
      color: convertColor(outputs.Pivot, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.r1Stroke = {
      color: convertColor(outputs["Resistance 1"], "#b82c0c"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.s1Stroke = {
      color: convertColor(outputs["Support 1"], "#69918"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Supertrend (2 lines)
  else if (normalizedType === "supertrend") {
    appearanceConfig.uptrendStroke = {
      color: convertColor(outputs.Uptrend, "#8cc176"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.downtrendStroke = {
      color: convertColor(outputs.Downtrend, "#b82c0c"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // ZigZag (single line)
  else if (normalizedType === "zigzag" || normalizedType === "zig zag") {
    const color = convertColor(outputs.Result, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // ATR Trailing Stop (2 lines)
  else if (
    normalizedType === "atr trailing stop" ||
    normalizedType === "atr stop"
  ) {
    appearanceConfig.buyStroke = {
      color: convertColor(outputs["Buy Stops"], "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.sellStroke = {
      color: convertColor(
        outputs["Sell Stops"]?.color || outputs["Sell Stops"],
        "#00bff0",
      ),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Williams A/D (single line)
  else if (
    normalizedType === "w acc dist" ||
    normalizedType === "williams acc dist"
  ) {
    const color = convertColor(outputs.Result, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // High Low (3 lines - top, median, bottom)
  else if (normalizedType === "high low") {
    appearanceConfig.topStroke = {
      color: convertColor(outputs["High Low Top"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.middleStroke = {
      color: convertColor(outputs["High Low Median"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.bottomStroke = {
      color: convertColor(outputs["High Low Bottom"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Median Price (single line)
  else if (
    normalizedType === "med price" ||
    normalizedType === "median price"
  ) {
    const color = convertColor(outputs.Result, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Keltner (3 lines - top, median, bottom)
  else if (normalizedType === "keltner") {
    appearanceConfig.topStroke = {
      color: convertColor(outputs["Keltner Top"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.middleStroke = {
      color: convertColor(outputs["Keltner Median"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.bottomStroke = {
      color: convertColor(outputs["Keltner Bottom"], "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Bollinger Bandwidth (single line)
  else if (
    normalizedType === "boll bw" ||
    normalizedType === "bollinger bandwidth"
  ) {
    const color = convertColor(outputs.Bandwidth, "#2196F3");
    appearanceConfig.stroke = {
      color,
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Klinger (2 lines)
  else if (normalizedType === "klinger") {
    appearanceConfig.klingerStroke = {
      color: convertColor(outputs.Klinger, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.signalStroke = {
      color: convertColor(
        outputs.KlingerSignal?.color || outputs.KlingerSignal,
        "#0056a4",
      ),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Volume Profile (bars color)
  else if (
    normalizedType === "vol profile" ||
    normalizedType === "volume profile"
  ) {
    appearanceConfig.barsColor = convertColor(outputs["Bars Color"], "#b64a96");
  }
  // Trend Intensity (single line)
  else if (
    normalizedType === "trend int" ||
    normalizedType === "trend intensity"
  ) {
    const color = convertColor(
      outputs.Result || outputs["Trend Intensity"],
      "#2196F3",
    );
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Volume Rate of Change (single line)
  else if (normalizedType === "vol roc" || normalizedType === "volume roc") {
    const color = convertColor(
      outputs.Result || outputs["Volume ROC"],
      "#2196F3",
    );
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Stochastic Momentum (2 lines)
  else if (
    normalizedType === "stch mtm" ||
    normalizedType === "stochastic momentum"
  ) {
    appearanceConfig.kStroke = {
      color: convertColor(outputs["%K"] || outputs.K, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.dStroke = {
      color: convertColor(outputs["%D"] || outputs.D, "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Valuation Lines (multiple lines)
  else if (
    normalizedType === "val lines" ||
    normalizedType === "valuation lines"
  ) {
    appearanceConfig.highStroke = {
      color: convertColor(outputs.High || outputs["Upper Line"], "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.lowStroke = {
      color: convertColor(outputs.Low || outputs["Lower Line"], "#00DD00"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Trade Volume Index (single line)
  else if (
    normalizedType === "trade vol" ||
    normalizedType === "trade volume"
  ) {
    const color = convertColor(outputs.Result || outputs.TVI, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // PMO (2 lines)
  else if (normalizedType === "pmo") {
    appearanceConfig.pmoStroke = {
      color: convertColor(outputs.PMO, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.signalStroke = {
      color: convertColor(outputs.Signal, "#FF0000"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Ultimate Oscillator (single line)
  else if (
    normalizedType === "ultimate" ||
    normalizedType === "ultimate oscillator"
  ) {
    const color = convertColor(outputs.Result || outputs.Ultimate, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Negative Volume Index (single line)
  else if (
    normalizedType === "neg vol" ||
    normalizedType === "negative volume"
  ) {
    const color = convertColor(outputs.Result || outputs.NVI, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Bollinger %b (single line)
  else if (normalizedType === "boll %b" || normalizedType === "bollinger %b") {
    const color = convertColor(outputs.Result || outputs["%b"], "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Disparity (single line)
  else if (normalizedType === "disparity") {
    const color = convertColor(outputs.Result || outputs.Disparity, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Typical Price (single line)
  else if (normalizedType === "typical price") {
    const color = convertColor(outputs.Result, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Positive Volume Index (single line)
  else if (
    normalizedType === "pos vol" ||
    normalizedType === "positive volume"
  ) {
    const color = convertColor(outputs.Result || outputs.PVI, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Mass Index (single line)
  else if (normalizedType === "mass idx" || normalizedType === "mass index") {
    const color = convertColor(
      outputs.Result || outputs["Mass Index"],
      "#2196F3",
    );
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Coppock Curve (single line)
  else if (normalizedType === "coppock") {
    const color = convertColor(outputs.Result || outputs.Coppock, "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Price ROC (single line)
  else if (normalizedType === "price roc") {
    const color = convertColor(
      outputs.Result || outputs["Price ROC"],
      "#2196F3",
    );
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Darvas Box (2 lines - top and bottom)
  else if (normalizedType === "darvas" || normalizedType === "darvas box") {
    appearanceConfig.topStroke = {
      color: convertColor(outputs["Box Top"] || outputs.Top, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
    appearanceConfig.bottomStroke = {
      color: convertColor(outputs["Box Bottom"] || outputs.Bottom, "#2196F3"),
      dash: "Solid",
      width: 1,
      opacity: 1,
    };
  }
  // Historical Volatility (single line)
  else if (
    normalizedType === "hist vol" ||
    normalizedType === "historical volatility"
  ) {
    const color = convertColor(
      outputs.Result || outputs["Historical Volatility"],
      "#2196F3",
    );
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Standard Deviation (single line)
  else if (
    normalizedType === "std dev" ||
    normalizedType === "standard deviation"
  ) {
    const color = convertColor(outputs.Result || outputs["Std Dev"], "#2196F3");
    appearanceConfig.stroke = { color, dash: "Solid", width: 1, opacity: 1 };
  }
  // Others - default single line
  else {
    const firstOutput = Object.values(outputs)[0];
    if (firstOutput) {
      appearanceConfig.stroke = {
        color: convertColor(firstOutput, "#2196F3"),
        dash: "Solid",
        width: 1,
        opacity: 1,
      };
    }
  }

  return appearanceConfig;
};

/**
 * Convert legacy MA type to Odin format
 * @param {string} legacyType - Legacy MA type (ma, ema, sma, wma, etc.)
 * @returns {string} - Odin MA type
 */
const convertMAType = (legacyType) => {
  // Tabela AUTORITATIVA: `CIQ.Studies.movingAverage.conversions`
  // (HtmlChart/temp/calcsChartIQ.js:1592-1602). Aceita as duas grafias que
  // aparecem em template salvo — o "study type" (ma/sma/ema/tsma/tma/vma/vdma/
  // wma/smma) e o "type name" exibido (simple/exponential/time series/
  // triangular/variable/vidya/weighted/welles wilder).
  //
  // Antes só simple/exponential/weighted eram conhecidos e TODO o resto caía no
  // fallback `sma`: uma Triangular ou Welles Wilder salva no legado virava média
  // simples, calada. Todos os alvos existem em `getMACalculator`.
  const typeMap = {
    ma: "sma",
    simple: "sma",
    sma: "sma",
    exponential: "ema",
    ema: "ema",
    weighted: "wma",
    wma: "wma",
    triangular: "tma",
    tma: "tma",
    "time series": "tsma",
    tsma: "tsma",
    variable: "vma",
    vma: "vma",
    vidya: "vidya",
    vdma: "vidya",
    // "welles wilder" do ChartIQ = suavização de Wilder = `smma` do odin. É o
    // mesmo destino do FML.WMA do Silverlight (que é WilderMA, não ponderada).
    "welles wilder": "smma",
    smma: "smma",
  };
  return typeMap[(legacyType || "sma").toLowerCase()] || "sma";
};

/**
 * `Offset` do ChartIQ → `offsetBars` do odin — deslocamento no TEMPO, em barras
 * (`studiesChartIQ.js:38`, aplicado em `calculateMovingAverage`,
 * `calcsChartIQ.js:456`, e na variante Time Series, `:1351`).
 *
 * ⚠️ NÃO confundir com os "offset/shift" de PREÇO deste mesmo arquivo:
 * `Shift Percentage` → `highShift`/`lowShift` (envelope % do High Low) e
 * `Shift`/`Multiplier` → `shift` (Keltner). Mapear um no outro transforma um
 * envelope percentual em deslocamento temporal, sem erro nenhum na tela.
 *
 * Faixa do engine (`AddParam("DESLOCAR", … -10000, 10000)`); valor inválido → 0
 * (identidade), nunca NaN — NaN vazaria para o `renderKey` do indicador.
 */
const OFFSET_BARS_LIMIT = 10000;
const convertOffsetBars = (raw) => {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-OFFSET_BARS_LIMIT, Math.min(OFFSET_BARS_LIMIT, n));
};

/**
 * Map legacy indicator type to Odin Sight indicator type
 * @param {string} legacyType - Legacy indicator type/name
 * @returns {string|null} - Odin Sight indicator key or null if not supported
 */
const mapLegacyIndicatorType = (legacyType) => {
  const indicatorMap = {
    // Moving Averages
    ma: "ma", // Generic MA - will determine type from inputs
    sma: "sma",
    ema: "ema",
    wma: "wma",
    tma: "tma",
    dema: "dema",
    vidya: "vidya",
    tsma: "tsma",
    vma: "vma",
    smma: "smma",
    "typical price": "typicalPrice",
    "rainbow ma": "rainbowMA",

    // Trend
    macd: "macd",
    adx: "adx",
    hiloactivator: "hiloActivator",
    "hilo activator": "hiloActivator",
    supertrend: "supertrend",
    "atr trailing stop": "atrStop",
    "atr stop": "atrStop",
    psar: "sar",
    sar: "sar",
    supportresistance: "supportResistance",
    "support resistance": "supportResistance",
    zigzag: "zigzag",
    "zig zag": "zigzag",
    "ichimoku clouds": "ichimoku",
    ichimoku: "ichimoku",
    alligator: "alligator",
    "time fcst": "linearForecast",
    "lin fcst": "linearForecast",
    "linear forecast": "linearForecast",
    "pivot points": "pivotPoints",
    "val lines": "valuationLines",
    "valuation lines": "valuationLines",
    darvas: "darvasBox",
    "darvas box": "darvasBox",

    // Oscillators
    rsi: "rsi",
    "m flow": "mfi",
    "money flow index": "mfi",
    mfi: "mfi",
    atr: "atr",
    "williams %r": "williamsR",
    cci: "cci",
    stochastics: "stochastic",
    stochastic: "stochastic",
    "stch mtm": "stochasticMomentum",
    "stochastic momentum": "stochasticMomentum",
    didi: "didi",
    "rel vig": "rvi",
    rvi: "rvi",
    awesome: "awesomeOscillator",
    "awesome oscillator": "awesomeOscillator",
    aroon: "aroon",
    "aroon osc": "aroon",
    "rel vol": "relativeVolatility",
    "relative volatility": "relativeVolatility",
    vortex: "vortex",
    "pring kst": "kst",
    kst: "kst",
    "ma dev": "maDeviation",
    "ma deviation": "maDeviation",
    "boll bw": "bollingerBandwidth",
    "bollinger bandwidth": "bollingerBandwidth",
    "boll %b": "bollingerPercentB",
    "bollinger %b": "bollingerPercentB",
    ravi: "ravi",
    schaff: "schaffTrendCycle",
    "schaff trend cycle": "schaffTrendCycle",
    stc: "schaffTrendCycle",
    shinohara: "shinohara",
    "trend int": "trendIntensity",
    "trend intensity": "trendIntensity",
    pmo: "pmo",
    ultimate: "ultimateOscillator",
    "ultimate oscillator": "ultimateOscillator",
    disparity: "disparity",
    "price osc": "priceOscillator",
    "price oscillator": "priceOscillator",
    psy: "psy",
    "intraday mtm": "intradayMomentum",
    "intraday momentum": "intradayMomentum",
    "bal pwr": "balanceOfPower",
    "balance of power": "balanceOfPower",
    "ehler fisher": "ehlerFisher",
    "chande mtm": "chandeMomentum",
    "chande momentum": "chandeMomentum",
    twiggs: "twiggsMF",
    "twiggs mf": "twiggsMF",
    swing: "swingIndex",
    "swing index": "swingIndex",

    // Volume
    "elder force": "forceIndex",
    forceindex: "forceIndex",
    "forceindex (2 averages)": "forceIndex",
    "on bal vol": "onBalanceVolume",
    "on balance volume": "onBalanceVolume",
    obv: "onBalanceVolume",
    "trade vol": "tradeVolumeIndex",
    "trade volume": "tradeVolumeIndex",
    "neg vol": "negativeVolumeIndex",
    "negative volume": "negativeVolumeIndex",
    "pos vol": "positiveVolumeIndex",
    "positive volume": "positiveVolumeIndex",
    "price vol": "pvt",
    pvt: "pvt",
    "w acc dist": "williamsad",
    "williams acc dist": "williamsad",
    "vol profile": "volumeProfile",
    "volume profile": "volumeProfile",
    vwap: "vwap",
    avwap: "vwap",
    klinger: "klinger",
    volume: "volume",
    "vol undr": "volume",
    "vol osc": "volumeOscillator",
    "volume oscillator": "volumeOscillator",
    "vol roc": "volumeRateOfChange",

    // Momentum
    momentum: "momentum",
    "elder ray": "elderRay",
    trix: "trix",
    "price roc": "priceRateOfChange",
    "lin r2": "linearRegressionR2",
    coppock: "coppock",

    // Volatility
    "bollinger bands": "bollingerBand",
    bollingerbands: "bollingerBand",
    "donchian channel": "donchianChannel",
    donchian: "donchianChannel",
    "atr bands": "atrbands",
    keltner: "keltner",
    "starc bands": "starcBands",
    "ma env": "maEnvelope",
    "ma envelope": "maEnvelope",
    "high low": "highLow",
    "high-low": "highLow",
    hhv: "hhv",
    llv: "llv",
    "hist vol": "historicalVolatility",
    "historical volatility": "historicalVolatility",
    "std dev": "standardDeviation",
    "standard deviation": "standardDeviation",
    "mass idx": "massIndex",
    "mass index": "massIndex",

    // Comparison
    compare: "compare",
    comparison: "compare",
  };

  const normalized = (legacyType || "").toLowerCase().trim();
  return indicatorMap[normalized] || null;
};

/**
 * Convert legacy drawings to Odin Sight format
 * @param {Array} legacyDrawings - Array of legacy drawings
 * @returns {Array} - Array of Odin Sight drawings
 */
const convertDrawings = (legacyDrawings) => {
  // TODO: Implement drawing conversion
  // Map legacy drawing types and coordinates to current format

  console.log("[LegacyConverter] Converting drawings:", legacyDrawings.length);

  return legacyDrawings.map((drawing, index) => {
    // Basic structure - to be refined based on actual legacy format
    return {
      id: `drawing-${Date.now()}-${index}`,
      type: mapLegacyDrawingType(drawing.type),
      coordinates: drawing.coordinates || [],
      // Add more fields as needed based on Odin Sight drawing structure
    };
  });
};

/**
 * Map legacy drawing type to Odin Sight drawing type
 * @param {string} legacyType - Legacy drawing type
 * @returns {string} - Odin Sight drawing type
 */
const mapLegacyDrawingType = (legacyType) => {
  // TODO: Implement drawing type mapping

  const drawingMap = {
    trendline: "trendline",
    horizontal: "horizontalLine",
    vertical: "verticalLine",
    fibonacci: "fibonacciRetracement",
    rectangle: "rectangle",
    channel: "channel",
    // Add more mappings as needed...
  };

  const normalized = (legacyType || "").toLowerCase();
  return drawingMap[normalized] || legacyType;
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate converted workspace
 * @param {Object} workspace - Converted workspace
 * @returns {boolean} - Validation result
 */
export const validateConvertedWorkspace = (workspace) => {
  if (!workspace) {
    console.error("[LegacyConverter] Workspace is null or undefined");
    return false;
  }

  if (!workspace.charts || !Array.isArray(workspace.charts)) {
    console.error("[LegacyConverter] Invalid charts array");
    return false;
  }

  if (workspace.charts.length === 0) {
    console.warn("[LegacyConverter] Workspace has no charts");
    return false;
  }

  for (const chart of workspace.charts) {
    if (!chart.id || !chart.symbol) {
      console.error("[LegacyConverter] Invalid chart structure:", chart);
      return false;
    }
  }

  return true;
};

/**
 * Get conversion statistics
 * @param {Object} legacyData - Original legacy data
 * @param {Object} workspace - Converted workspace
 * @returns {Object} - Conversion statistics
 */
export const getConversionStats = (legacyData, workspace) => {
  const stats = {
    charts: workspace.charts.length,
    indicators: 0,
    drawings: 0,
    skippedIndicators: conversionIssues.skipped || [],
    failedIndicators: conversionIssues.failed || [],
  };

  for (const chart of workspace.charts) {
    stats.indicators += chart.indicators?.length || 0;
    stats.drawings += chart.drawings?.length || 0;
  }

  return stats;
};
